import { Avatar, IconButton, ListItemAvatar, Typography } from '@mui/material';
import { ComponentType } from 'react';
import {
  concatUserPosition,
  getUserFullName,
  getUserPosition,
  parseUserInviteStatus,
  resizeImage,
} from 'helpers';
import { User, WorkRecord } from 'store/interfaces';
import { OverflowTip } from 'components/Tooltip';
import { RowSecondaryActionProps } from './interfaces/UserSelectDialogProps';
import {
  List,
  ListItem,
  ListItemText,
  Position,
  StyledPrimaryText,
} from './UserSelectDialog.styled';
import { DeleteOutlineIcon } from '../Icons';
import {
  ExternalUserDraftLabel,
  ExternalUserLabel,
  ExternalUserPendingLabel,
} from '../Label';

interface Props {
  enableSelfAssessment: boolean;
  autoAssignPmReviewer: boolean;
  scorecardOwner: User;
  users: User[];
  workRecord: WorkRecord;
  careerMentor: User;
  /** Users that should render without a delete button (existing reviewers
   * in add-only mode). */
  lockedUserIds?: number[];
  /** When provided, replaces the default right-side cell (delete / lock).
   * Caller renders any combination of action button + delete button. */
  RowSecondaryAction?: ComponentType<RowSecondaryActionProps>;
  /** Optional inline slot rendered after the name + badges in the primary
   * text (e.g. the Key Reviewer star). */
  PrimarySuffix?: ComponentType<{ user: User }>;
  onRemoveUser: (user: User) => void;
}

export default function ReviewersList({
  enableSelfAssessment,
  autoAssignPmReviewer,
  scorecardOwner,
  users,
  onRemoveUser,
  workRecord,
  careerMentor,
  lockedUserIds,
  RowSecondaryAction,
  PrimarySuffix,
}: Props) {
  const lockedSet = new Set(lockedUserIds ?? []);
  const removeUser = (userToRemove: User) => onRemoveUser(userToRemove);

  const ownerPosition = concatUserPosition(
    workRecord.position.name,
    workRecord.seniority,
  );

  const filteredUsers = autoAssignPmReviewer
    ? users.filter(({ id }) => id !== careerMentor.id)
    : users;

  return (
    <List>
      {enableSelfAssessment && (
        <ListItem
          key={scorecardOwner.id}
          id={`selected-user-${scorecardOwner.id}`}
        >
          <ListItemAvatar>
            <Avatar
              alt={scorecardOwner.fullName}
              src={resizeImage(scorecardOwner.avatar)}
            />
          </ListItemAvatar>
          <ListItemText
            disableTypography={false}
            primaryTypographyProps={{ variant: 'h8' }}
            secondaryTypographyProps={{ variant: 'body6' }}
            primary={scorecardOwner.fullName}
            secondary={
              <>
                {scorecardOwner.email}
                <Position>{ownerPosition}</Position>
              </>
            }
          />
        </ListItem>
      )}
      {autoAssignPmReviewer && careerMentor && (
        <ListItem key={careerMentor.id} id={`selected-user-${careerMentor.id}`}>
          <ListItemAvatar>
            <Avatar
              alt={careerMentor.fullName}
              src={resizeImage(careerMentor.avatar)}
            />
          </ListItemAvatar>
          <ListItemText
            disableTypography={false}
            primaryTypographyProps={{ variant: 'h8' }}
            secondaryTypographyProps={{ variant: 'body6' }}
            primary={careerMentor.fullName}
            secondary={
              <>
                {careerMentor.email}
                <Position>{getUserPosition(careerMentor)}</Position>
              </>
            }
          />
        </ListItem>
      )}
      {filteredUsers.map((user) => {
        const { isExternalUser, isDraft, isPending, isAccepted } =
          parseUserInviteStatus(user);
        const userPosition = getUserPosition(user);
        const isLocked = user.id !== null && lockedSet.has(user.id as number);

        const defaultRemove = () => removeUser(user);
        const secondaryAction = RowSecondaryAction ? (
          <RowSecondaryAction
            user={user}
            isLocked={isLocked}
            defaultRemove={defaultRemove}
          />
        ) : isLocked ? undefined : (
          <IconButton onClick={defaultRemove}>
            <DeleteOutlineIcon color="primary" />
          </IconButton>
        );

        return (
          <ListItem
            key={user.id}
            id={`selected-user-${user.id}`}
            secondaryAction={secondaryAction}
          >
            <ListItemAvatar>
              <Avatar alt={user.fullName} src={resizeImage(user.avatar)} />
            </ListItemAvatar>
            <ListItemText
              disableTypography={false}
              primaryTypographyProps={{ variant: 'h8' }}
              secondaryTypographyProps={{ variant: 'body6' }}
              primary={
                <StyledPrimaryText>
                  <Typography variant="h8" component="span">
                    {getUserFullName(user)}
                  </Typography>
                  {isExternalUser && <ExternalUserLabel />}
                  {isPending && <ExternalUserPendingLabel />}
                  {isDraft && <ExternalUserDraftLabel />}
                  {PrimarySuffix && <PrimarySuffix user={user} />}
                </StyledPrimaryText>
              }
              secondary={
                isExternalUser && !isAccepted ? null : (
                  <OverflowTip>
                    {user.email}
                    {userPosition && (
                      <Position>{getUserPosition(user)}</Position>
                    )}
                  </OverflowTip>
                )
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
}
